import { trpc } from '@/lib/trpc'
import { isAdminIdentifier } from '@coinsrubot/shared'

/** The admin predicate used across the app (role OR identifier list). */
export function useIsAdmin(): boolean {
  const { data: me } = trpc.auth.me.useQuery()
  return !!me && (me.role === 'admin' || isAdminIdentifier(me.telegramId, me.telegramUsername, me.email))
}

/**
 * Which design the current visitor sees.
 *
 * `new_design` = "1" → v2 for everyone. While it is "0" (default) admins still
 * get v2 so the owner can review it, and everybody else keeps the classic
 * design. Flipping the setting in the admin panel switches the whole site on
 * the next page load; flipping it back is the instant rollback.
 */
export function useUiMode() {
  const isAdmin = useIsAdmin()
  const { data: design } = trpc.site.design.useQuery(undefined, { staleTime: 60_000 })

  // Local QA escape hatch — stripped from production builds, so it can never
  // leak to real visitors. Lets a developer preview v2 without an admin session:
  //   · open any page with ?ui=v2   (works in every new tab / origin), or
  //   · localStorage.setItem('coinsrubot_ui_v2', '1')
  let devPreview = false
  if ((import.meta as any).env?.DEV) {
    try {
      const flags = new URLSearchParams(window.location.search)
      devPreview = flags.get('ui') === 'v2' || localStorage.getItem('coinsrubot_ui_v2') === '1'
    } catch {
      devPreview = false
    }
  }

  return { isAdmin, v2: isAdmin || devPreview || design?.newDesign === true, design }
}

/**
 * Telegram Stars is admin-only until the owner opens it (app_settings
 * `stars_public`): admins always see it, everyone else only when the setting is
 * "1". Instant to flip from the admin panel — no deploy.
 */
export function useStarsAccess() {
  const isAdmin = useIsAdmin()
  const { data } = trpc.site.stars.useQuery(undefined, { staleTime: 60_000 })
  return { isAdmin, starsAllowed: isAdmin || data?.starsPublic === true }
}
